import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Flag, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const FOLLOWUP_LABELS = {
  billing: "Needs billing",
  parts: "Order parts",
  callback: "Call back",
  other: "Follow up",
};

export const FOLLOWUP_STYLES = {
  billing: "bg-emerald-50 text-emerald-800 border-emerald-300",
  parts: "bg-amber-50 text-amber-800 border-amber-300",
  callback: "bg-blue-50 text-blue-800 border-blue-300",
  other: "bg-slate-100 text-slate-700 border-slate-300",
};

export const FollowupPanel = ({ ticket, onChange }) => {
  const [type, setType] = useState("billing");
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const fu = ticket.followup;

  const flag = async () => {
    setBusy(true);
    try {
      await api.post(`/tickets/${ticket.id}/followup`, { type, note, due_date: due || null });
      toast.success("Follow-up flagged");
      setNote(""); setDue("");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const done = async () => {
    setBusy(true);
    try {
      await api.delete(`/tickets/${ticket.id}/followup`);
      toast.success("Follow-up cleared");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (fu) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="followup-active">
        <div className="flex items-center gap-2">
          <Flag className="w-4 h-4 text-amber-700" />
          <span className="text-sm font-semibold text-amber-900">{FOLLOWUP_LABELS[fu.type] || fu.type}</span>
          {fu.due_date && (
            <span className={`text-xs ml-auto font-medium ${fu.due_date < new Date().toISOString().slice(0, 10) ? "text-red-700" : "text-amber-800"}`}>
              Due {fu.due_date}
            </span>
          )}
        </div>
        {fu.note && <div className="text-sm text-amber-900 whitespace-pre-wrap">{fu.note}</div>}
        <Button size="sm" onClick={done} disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 gap-2" data-testid="followup-done-btn">
          <CheckCircle2 className="w-4 h-4" /> Mark done
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="followup-form">
      <Select value={type} onValueChange={setType}>
        <SelectTrigger data-testid="followup-type"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(FOLLOWUP_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <Textarea rows={2} placeholder="Optional note (e.g. 'Invoice for $220', 'Order drain pump')" value={note} onChange={(e) => setNote(e.target.value)} data-testid="followup-note" />
      <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="followup-due" />
      <Button onClick={flag} disabled={busy} className="w-full bg-blue-700 hover:bg-blue-800 gap-2" data-testid="followup-flag-btn">
        <Flag className="w-4 h-4" /> Flag for follow-up
      </Button>
    </div>
  );
};
